import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Icon, Link, Text } from '@amzn/storm-ui';
import { LinkProps } from '@amzn/aac-chrome-internal-api';
import { circleQuestion, externalLinkAlt } from '@amzn/storm-ui-icons';
import { selectHelpMenu } from 'src/features/header/store/slice';
import { RootState } from 'src/app/store';
import styled from 'styled-components';
import { inkwell } from 'src/colors';
import {
  FocusModeUtilitiesIconButton,
  UtilitiesIcon,
  UtilitiesIconButton,
} from '../styles';
import { HelpDropdownPublisher } from 'src/commons/utils/metrics';
import { CustomPopper } from '../CustomPopper/CustomPopper';
import { PAGE_LAYOUT_TYPE, selectViewType, VIEW_TYPE } from 'src/app/slice';
import { useDynamicIntroWidgetsContext } from 'src/providers/DynamicIntroWidgetsContextProvider';
import { useAccountDropdownContext } from '../AccountDropdown/AccountDropdownProvider';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider/InternalChromeConfigContextProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

const featureCenterUid = 'aac-help-menu-item-feature-center';
const supportCenterUid = 'aac-help-menu-item-support-center';
const helpMenuDropdownId = 'aac-support-center-dropdown';

const StyledHelpMenuLink = styled(Link)`
  &&&& {
    display: block;
    padding: ${({ theme }) => theme.spacing.small};
    text-decoration: none;
    color: ${inkwell};

    &:hover {
      background-color: rgba(111, 111, 111, 0.1);
      text-decoration: none;
      color: ${inkwell};
    }
  }
`;

const HelpMenuLink: React.VFC<{ helpLinks: LinkProps[] }> = ({ helpLinks }) => {
  const { hasAccessToSA, hasAccessToDSP } = useAccountDropdownContext();

  return (
    <>
      {helpLinks.map(({ label, url, isExternal, uid }) => (
        <StyledHelpMenuLink
          key={label}
          href={
            uid === supportCenterUid
              ? `${url}${
                  url.includes('?') ? '&' : '?'
                }hasAccessToSA=${hasAccessToSA}&hasAccessToDSP=${hasAccessToDSP}`
              : url
          }
          target="_blank"
          rel="noreferrer"
          data-takt-id={uid}
        >
          <Text>
            {label} {isExternal === true && <Icon type={externalLinkAlt} />}
          </Text>
        </StyledHelpMenuLink>
      ))}
    </>
  );
};

interface HelpDropdownProps {
  helpInfo: Array<LinkProps>;
  viewType: VIEW_TYPE;
}

const HelpDropdown: React.VFC<HelpDropdownProps> = ({ helpInfo, viewType }) => {
  const chromeConfig = useChromeConfigContext();
  const [isOpen, setIsOpen] = useState(false);
  const { loadFitFunction } = useDynamicIntroWidgetsContext();
  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  const handleToggle = () => {
    if (isOpen) {
      HelpDropdownPublisher.publishCounterMonitor('Close', 1);
    } else {
      HelpDropdownPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
    loadFitFunction();
  };
  const conditionalLoadFit =
    viewType === VIEW_TYPE.DESKTOP ? loadFitFunction : undefined;
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const StyledIconButton =
    chromeConfig.pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS
      ? FocusModeUtilitiesIconButton
      : UtilitiesIconButton;

  const isHMHIngressEnabled = useIsFeatureEnabled(
    WeblabedFeatures.HMH_IN_HEADER_QUESTION_MARK,
  );

  const helpLinks = helpInfo.filter((helpLink) => {
    if (internalChromeConfig.retailerId || isHMHIngressEnabled) {
      return (
        helpLink.uid !== featureCenterUid &&
        helpLink.uid !== 'aac-help-menu-item-ads-status'
      );
    }
    return helpLink.uid !== featureCenterUid;
  });

  return (
    <CustomPopper
      zIndex={500}
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <StyledIconButton
          onClick={handleToggle}
          onMouseEnter={conditionalLoadFit}
          onFocus={conditionalLoadFit}
          active={isOpen}
          aria-label={helpInfo?.[0]?.label}
          aria-haspopup={true}
          aria-expanded={isOpen}
          data-takt-id={helpMenuDropdownId}
          data-ccx-e2e-id={helpMenuDropdownId}
        >
          <UtilitiesIcon type={circleQuestion} />
        </StyledIconButton>
      }
      position="bottom"
      align="center"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={-10}
    >
      <div data-ccx-e2e-id="support-center">
        <HelpMenuLink helpLinks={helpLinks} />
      </div>
    </CustomPopper>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    helpInfo: selectHelpMenu(state),
    viewType: selectViewType(state),
  };
};

export default connect(mapStateToProps)(HelpDropdown);
